import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { UserRole, Application, Ticket, InventoryItem, Transaction } from './types';
import { backendService } from './services/backendService';

// Layout Components
import { Navbar, Footer, ProtectedRoute } from './src/components/layout';

// UX Components
import { ErrorBoundary, LoadingPage } from './src/components/ui';

// Lazy load page components for code splitting
const LandingPage = lazy(() => import('./src/components/pages/public/LandingPage').then(m => ({ default: m.LandingPage })));
const AboutPage = lazy(() => import('./src/components/pages/public/AboutPage').then(m => ({ default: m.AboutPage })));
const CareersPage = lazy(() => import('./src/components/pages/public/CareersPage').then(m => ({ default: m.CareersPage })));
const BlogPage = lazy(() => import('./src/components/pages/public/BlogPage').then(m => ({ default: m.BlogPage })));
const LegalPage = lazy(() => import('./src/components/pages/public/LegalPage').then(m => ({ default: m.LegalPage })));
const SignLease = lazy(() => import('./src/components/pages/public/SignLease').then(m => ({ default: m.SignLease })));
const SetupPayment = lazy(() => import('./src/components/pages/public/SetupPayment').then(m => ({ default: m.SetupPayment })));
const AcceptLeaseUpdate = lazy(() => import('./src/components/pages/public/AcceptLeaseUpdate').then(m => ({ default: m.AcceptLeaseUpdate })));
const MarketplaceLead = lazy(() => import('./src/components/pages/public/MarketplaceLead'));
const BusinessSignup = lazy(() => import('./src/components/pages/public/BusinessSignup').then(m => ({ default: m.BusinessSignup })));
const OEMLandingPage = lazy(() => import('./src/components/pages/public/OEMLandingPage').then(m => ({ default: m.OEMLandingPage })));
const ContactPage = lazy(() => import('./src/components/pages/public/ContactSection'));
// PropertyInquiryForm removed - now in housing-app subdomain

const CustomerLogin = lazy(() => import('./src/components/pages/auth/CustomerLogin').then(m => ({ default: m.CustomerLogin })));
const AdminLogin = lazy(() => import('./src/components/pages/auth/AdminLogin').then(m => ({ default: m.AdminLogin })));
const ApplicationForm = lazy(() => import('./src/components/pages/auth/ApplicationForm').then(m => ({ default: m.ApplicationForm })));

const UnifiedCustomerDashboard = lazy(() => import('./src/components/pages/customer/UnifiedCustomerDashboard').then(m => ({ default: m.UnifiedCustomerDashboard })));
const ConditionReport = lazy(() => import('./src/components/pages/customer/ConditionReport').then(m => ({ default: m.ConditionReport })));

const AdminDashboard = lazy(() => import('./src/components/pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const CommandCenter = lazy(() => import('./src/components/pages/admin/CommandCenter').then(m => ({ default: m.CommandCenter })));
const FinancialDashboard = lazy(() => import('./src/components/pages/admin/FinancialDashboard').then(m => ({ default: m.FinancialDashboard })));
const CRMDashboard = lazy(() => import('./src/components/pages/admin/CRMDashboard').then(m => ({ default: m.CRMDashboard })));
const CustomerManager = lazy(() => import('./src/components/pages/admin/CustomerManager').then(m => ({ default: m.CustomerManager })));
const ApplicationManager = lazy(() => import('./src/components/pages/admin/ApplicationManager').then(m => ({ default: m.ApplicationManager })));
const InstallationManager = lazy(() => import('./src/components/pages/admin/ApplicationManager').then(m => ({ default: m.InstallationManager })));
const TicketDashboard = lazy(() => import('./src/components/pages/admin/TicketDashboard').then(m => ({ default: m.TicketDashboard })));
const MessagingCenter = lazy(() => import('./src/components/pages/admin/MessagingCenter').then(m => ({ default: m.MessagingCenter })));
const InventoryDashboard = lazy(() => import('./src/components/pages/admin/ApplicationManager').then(m => ({ default: m.InventoryDashboard })));
const AnalyticsDashboard = lazy(() => import('./src/components/pages/admin/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const NotificationCenter = lazy(() => import('./src/components/pages/admin/NotificationCenter').then(m => ({ default: m.NotificationCenter })));
const StaffManager = lazy(() => import('./src/components/pages/admin/StaffManager').then(m => ({ default: m.StaffManager })));
const MaintenanceScheduler = lazy(() => import('./src/components/pages/admin/MaintenanceScheduler').then(m => ({ default: m.MaintenanceScheduler })));
const RouteOptimizer = lazy(() => import('./src/components/pages/admin/RouteOptimizer').then(m => ({ default: m.RouteOptimizer })));
const LeaseTemplateEditor = lazy(() => import('./src/components/pages/admin/LeaseTemplateEditor').then(m => ({ default: m.LeaseTemplateEditor })));
const CompanySettings = lazy(() => import('./src/components/pages/admin/CompanySettings').then(m => ({ default: m.CompanySettings })));
const ContactMessages = lazy(() => import('./src/components/pages/admin/ContactMessages').then(m => ({ default: m.ContactMessages })));

// Super Admin Components
const SuperAdminDashboard = lazy(() => import('./src/components/pages/super-admin/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const SuperAdminLeads = lazy(() => import('./src/components/pages/super-admin/SuperAdminLeads').then(m => ({ default: m.SuperAdminLeads })));


// Legal content for pages
const TermsContent = (
    <>
        <p className="text-sm text-slate-500 mb-4"><em>Last Updated: January 10, 2026</em></p>

        <h2 className="text-xl font-bold mt-6 mb-3">1. Introduction & Acceptance</h2>
        <p>Welcome to Nestly Suite Inc. ("Company," "we," "us," "our"). By accessing or using our services, website, or applications, you ("Customer," "you," "your") agree to be bound by these Terms of Service. If you do not agree, discontinue use immediately.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">2. Services Provided</h2>
        <p>Nestly Suite provides washer and dryer rental services for residential use. Each rental is governed by a specific rental agreement in addition to these general terms.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">3. Appliance Leasing Terms</h2>
        <p><strong>3.1 Rental Period:</strong> Appliance rentals are month-to-month with no long-term commitment required. Minimum 6-month term applies for new installations.</p>
        <p><strong>3.2 Monthly Fee:</strong> Standard monthly rental is $50.00 for washer/dryer combo, due on billing anniversary date.</p>
        <p><strong>3.3 Equipment Use:</strong> Equipment is for residential use only at the designated installation address. Relocation requires prior written approval.</p>
        <p><strong>3.4 Maintenance & Repairs:</strong> We provide free repairs for normal wear and tear. Damage from misuse, negligence, or unauthorized modifications is the customer's responsibility at full replacement cost ($700.00).</p>
        <p><strong>3.5 Termination:</strong> Either party may terminate with 30 days written notice. Equipment must be returned in working condition. Failure to return equipment constitutes theft under Ohio law.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">4. Mandatory Insurance Requirements</h2>
        <p><strong>IMPORTANT:</strong> All customers MUST maintain valid renter's insurance covering:</p>
        <ul className="list-disc ml-6">
            <li>Water damage and flooding</li>
            <li>Personal liability (minimum $100,000)</li>
            <li>Property damage</li>
        </ul>
        <p>Failure to maintain insurance constitutes a material breach and grounds for immediate termination.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">5. Payment Terms</h2>
        <p><strong>5.1 Payment Methods:</strong> All payments are processed securely via Stripe. A valid payment method must remain on file at all times.</p>
        <p><strong>5.2 Late Fees:</strong> Late fees apply after a 10-day grace period.</p>
        <p><strong>5.3 Returned Payments:</strong> A $35.00 fee applies for returned payments or chargebacks.</p>
        <p><strong>5.4 Collections:</strong> Unpaid balances may be referred to collections and reported to credit bureaus.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">6. Limitation of Liability</h2>
        <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong></p>
        <p>Nestly Suite Inc. shall NOT be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses resulting from:</p>
        <ul className="list-disc ml-6">
            <li>Property damage from equipment malfunction (claimable through your renter's insurance)</li>
            <li>Service interruptions or delays</li>
            <li>Acts of God, natural disasters, or circumstances beyond our control</li>
            <li>Third-party actions or negligence</li>
        </ul>
        <p><strong>Our total liability shall not exceed the amount paid by you in the preceding 12 months.</strong></p>

        <h2 className="text-xl font-bold mt-6 mb-3">7. Indemnification</h2>
        <p>You agree to indemnify, defend, and hold harmless Nestly Suite Inc., its officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including attorney's fees) arising from your use of our services, violation of these terms, or infringement of any third-party rights.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">8. Dispute Resolution</h2>
        <p><strong>8.1 Binding Arbitration:</strong> Any dispute arising from these Terms or our services shall be resolved by binding arbitration in Summit County, Ohio, in accordance with the American Arbitration Association rules.</p>
        <p><strong>8.2 Class Action Waiver:</strong> You agree to resolve disputes on an individual basis only. Class actions and class arbitrations are not permitted.</p>
        <p><strong>8.3 Governing Law:</strong> These Terms are governed by the laws of the State of Ohio without regard to conflict of law provisions.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">9. Termination</h2>
        <p>We reserve the right to terminate or suspend your account and access to services at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other customers, us, or third parties.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">10. Modifications</h2>
        <p>We may modify these Terms at any time. Continued use of services after changes constitutes acceptance. Material changes will be communicated via email or portal notification.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">11. Contact Information</h2>
        <p>Nestly Suite Inc.<br />
            Email: legal@nestlysuite.com<br />
            Website: www.nestlysuite.com</p>
    </>
);

const PrivacyContent = (
    <>
        <p className="text-sm text-slate-500 mb-4"><em>Last Updated: January 10, 2026</em></p>

        <h2 className="text-xl font-bold mt-6 mb-3">1. Introduction</h2>
        <p>Nestly Suite Inc. ("Company," "we," "us," "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our residential stay services and appliance leasing services.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">2. Information We Collect</h2>
        <p><strong>2.1 Personal Information:</strong></p>
        <ul className="list-disc ml-6 mb-3">
            <li>Full name, address, phone number, email</li>
            <li>Date of birth and government-issued ID</li>
            <li>Social Security Number (for credit checks on residential leases)</li>
            <li>Employment and income information</li>
            <li>Emergency contact information</li>
        </ul>
        <p><strong>2.2 Financial Information:</strong></p>
        <ul className="list-disc ml-6 mb-3">
            <li>Bank account or credit/debit card details (processed via Stripe)</li>
            <li>Payment history and billing records</li>
            <li>Credit check results (for lease applications)</li>
        </ul>
        <p><strong>2.3 Property & Service Information:</strong></p>
        <ul className="list-disc ml-6 mb-3">
            <li>Installation address and access information</li>
            <li>Service requests and maintenance history</li>
            <li>Move-in/move-out condition reports with photos</li>
        </ul>
        <p><strong>2.4 Technical Information:</strong></p>
        <ul className="list-disc ml-6">
            <li>IP address, browser type, device information</li>
            <li>Website usage data and cookies</li>
        </ul>

        <h2 className="text-xl font-bold mt-6 mb-3">3. How We Use Your Information</h2>
        <ul className="list-disc ml-6">
            <li>Process applications and lease agreements</li>
            <li>Schedule and complete installations and maintenance</li>
            <li>Process payments and manage billing</li>
            <li>Communicate about service updates and account matters</li>
            <li>Comply with legal obligations</li>
            <li>Enforce our terms and protect against fraud</li>
            <li>Improve our services and customer experience</li>
        </ul>

        <h2 className="text-xl font-bold mt-6 mb-3">4. Information Sharing</h2>
        <p><strong>We DO NOT sell your personal information.</strong></p>
        <p>We may share data with:</p>
        <ul className="list-disc ml-6">
            <li><strong>Service Providers:</strong> Payment processors (Stripe), email services, background check providers</li>
            <li><strong>Property Owners:</strong> Master lease landlords (limited tenant information as required by lease)</li>
            <li><strong>Legal Authorities:</strong> When required by law, court order, or to protect rights and safety</li>
            <li><strong>Credit Bureaus:</strong> Payment history may be reported for residential leases</li>
        </ul>

        <h2 className="text-xl font-bold mt-6 mb-3">5. Data Security</h2>
        <p>We implement industry-standard security measures including:</p>
        <ul className="list-disc ml-6">
            <li>SSL/TLS encryption for all data transmission</li>
            <li>Encrypted storage for sensitive information</li>
            <li>Access controls and authentication measures</li>
            <li>Regular security audits and monitoring</li>
        </ul>
        <p className="mt-2"><strong>NOTE:</strong> No method of transmission or storage is 100% secure. We cannot guarantee absolute security.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">6. Data Retention</h2>
        <p>We retain your personal information for:</p>
        <ul className="list-disc ml-6">
            <li><strong>Active customers:</strong> Duration of service plus 7 years</li>
            <li><strong>Applications:</strong> 3 years from submission</li>
            <li><strong>Lease agreements:</strong> 7 years after termination (legal requirement)</li>
            <li><strong>Financial records:</strong> 7 years (tax requirements)</li>
        </ul>

        <h2 className="text-xl font-bold mt-6 mb-3">7. Your Rights</h2>
        <p>You have the right to:</p>
        <ul className="list-disc ml-6">
            <li>Access your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion (subject to legal retention requirements)</li>
            <li>Opt-out of marketing communications</li>
            <li>Obtain a copy of your data</li>
        </ul>
        <p className="mt-2">To exercise these rights, contact: privacy@nestlysuite.com</p>

        <h2 className="text-xl font-bold mt-6 mb-3">8. Children's Privacy</h2>
        <p>Our services are not intended for individuals under 18 years of age. We do not knowingly collect information from children.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">9. Third-Party Links</h2>
        <p>Our website may contain links to third-party sites. We are not responsible for the privacy practices of those sites.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">10. Updates to This Policy</h2>
        <p>We may update this policy periodically. Material changes will be communicated via email or website notice. Continued use constitutes acceptance of changes.</p>

        <h2 className="text-xl font-bold mt-6 mb-3">11. Contact Us</h2>
        <p>Nestly Suite Inc.<br />
            Email: privacy@nestlysuite.com<br />
            Website: www.nestlysuite.com</p>
    </>
);

const CookiesContent = (
    <>
        <p>We use cookies to enhance your experience on our website.</p>
        <p><strong>Essential Cookies:</strong> Required for the website to function (e.g., login sessions).</p>
        <p><strong>Analytics Cookies:</strong> Help us understand how visitors use our site to improve our services.</p>
        <p>You may disable cookies in your browser settings, but some features of the site may not work properly.</p>
    </>
);

const App = () => {
    const [role, setRole] = useState<UserRole>(UserRole.GUEST);
    const [applications, setApplications] = useState<Application[]>([]);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [currentAppId, setCurrentAppId] = useState<string | null>(null);
    // Sidebar state removed - now using top navigation
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);
    const [inventory, setInventory] = useState<InventoryItem[]>([]);
    const [transactions, setTransactions] = useState<Transaction[]>([]);

    // Super Admin view switching - allows switching between platform and company admin view
    const [viewAs, setViewAs] = useState<'SUPER_ADMIN' | 'ADMIN'>('SUPER_ADMIN');

    const navigate = useNavigate();

    const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    const idleTimerRef = React.useRef<NodeJS.Timeout | null>(null);

    const resetIdleTimer = () => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        if (user) { // Only set timer if user is logged in
            idleTimerRef.current = setTimeout(async () => {
                // Navigate first, then logout to avoid blank page
                navigate('/');
                await backendService.logout();
                setRole(UserRole.GUEST);
                setUser(null);
                setCurrentAppId(null);
                alert("You have been logged out due to inactivity.");
            }, IDLE_TIMEOUT);
        }
    };

    useEffect(() => {
        // Events to listen for activity
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

        const activityListener = () => resetIdleTimer();

        if (user) {
            events.forEach(event => window.addEventListener(event, activityListener));
            resetIdleTimer(); // Start timer immediately
        }

        return () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            events.forEach(event => window.removeEventListener(event, activityListener));
        };
    }, [user]); // Re-run when auth state changes

    useEffect(() => {
        const unsubscribe = backendService.monitorAuthState(async (u) => {
            if (u) {
                setUser(u);

                // Check for custom claims first (SaaS multi-tenant auth)
                try {
                    const tokenResult = await u.getIdTokenResult(true);
                    const claimsRole = tokenResult.claims.role as string;

                    // Small delay to ensure Firestore SDK picks up the refreshed token
                    await new Promise(resolve => setTimeout(resolve, 500));

                    if (claimsRole === 'super_admin') {
                        setRole(UserRole.SUPER_ADMIN);
                        fetchAdminData(UserRole.SUPER_ADMIN);
                        setLoading(false);
                        return;
                    }

                    if (claimsRole === 'admin') {
                        setRole(UserRole.ADMIN);
                        fetchAdminData(UserRole.ADMIN);
                        setLoading(false);
                        return;
                    }

                    if (claimsRole === 'staff') {
                        setRole(UserRole.STAFF);
                        fetchAdminData(UserRole.STAFF);
                        setLoading(false);
                        return;
                    }
                } catch (claimsError) {
                    console.log('No custom claims found, falling back to legacy auth');
                }

                // Legacy auth fallback
                const isAdminUser = await backendService.isAdmin(u.uid);
                const isStaffUser = await backendService.isStaff(u.uid);
                const isHardcodedAdmin = u.email === 'admin@nestlysuite.com';

                if (isAdminUser || isHardcodedAdmin) {
                    setRole(UserRole.ADMIN);
                    fetchAdminData(UserRole.ADMIN);
                } else if (isStaffUser) {
                    setRole(UserRole.STAFF);
                    fetchAdminData(UserRole.STAFF);
                } else {
                    const app = await backendService.getApplicationById(u.uid);
                    if (app) {
                        setApplications([app]);
                        setCurrentAppId(app.id);
                        setRole(UserRole.CUSTOMER);
                    }
                }
            } else {
                // User logged out - redirect to login if not on public page
                const publicPaths = ['/', '/login', '/apply', '/terms', '/privacy', '/housing-inquiry', '/sign-lease', '/setup-payment', '/lead'];
                const currentPath = window.location.hash.replace('#', '') || '/';
                const isPublicPath = publicPaths.some(p => currentPath.startsWith(p));

                if (!isPublicPath && role !== UserRole.GUEST) {
                    navigate('/login');
                }

                setRole(UserRole.GUEST);
                setUser(null);
            }
            setLoading(false);
        });
        return () => unsubscribe && unsubscribe();
    }, []);

    const fetchAdminData = async (userRole?: UserRole) => {
        setLoading(true);
        try {
            // Staff and Admin both get Applications, Tickets, Inventory
            const commonDataPromise = Promise.all([
                backendService.getAllApplications(),
                backendService.getAllTickets(),
                backendService.getInventory()
            ]);

            let txPromise: Promise<Transaction[]> | null = null;
            // Admin and Super Admin get transactions
            if (userRole === UserRole.ADMIN || userRole === UserRole.SUPER_ADMIN) {
                txPromise = backendService.getTransactions();
            }

            const [apps, tickets, inv] = await commonDataPromise;

            setApplications(apps);
            setTickets(tickets as Ticket[]);
            setInventory(inv);

            if (txPromise) {
                const txs = await txPromise;
                setTransactions(txs);
            }
        } catch (e) {
            console.error("Error fetching admin data:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        await backendService.logout();
        setRole(UserRole.GUEST);
        setCurrentAppId(null);
        navigate('/');
    };

    const createApplication = async (formData: any, password: string, files: any, presaleOrderId?: string) => {
        try {
            let newApp;
            if (presaleOrderId) {
                // Presale application submission
                newApp = await backendService.submitPresaleApplication(formData, password, files, presaleOrderId);
            } else {
                // Regular application submission
                newApp = await backendService.submitApplication(formData, password, files);
            }
            setApplications([newApp]);
            setCurrentAppId(newApp.id);
            setRole(UserRole.CUSTOMER);
            navigate('/dashboard');
        } catch (err: any) {
            console.error('Application submission error:', err);
            throw err;
        }
    };
    const handleCustomerLogin = async (email: string, pass: string) => {
        setLoading(true);
        try {
            const user = await backendService.login(email, pass);

            // Check if user is actually admin or staff trying to login here
            const isAdminUser = await backendService.isAdmin(user.uid);
            const isStaffUser = await backendService.isStaff(user.uid);
            const isHardcodedAdmin = user.email === 'admin@nestlysuite.com';

            if (isAdminUser || isHardcodedAdmin) {
                setRole(UserRole.ADMIN);
                await fetchAdminData(UserRole.ADMIN);
                navigate('/admin');
                return;
            }

            if (isStaffUser) {
                setRole(UserRole.STAFF);
                await fetchAdminData(UserRole.STAFF);
                navigate('/admin/maintenance'); // Default landing for staff
                return;
            }

            const app = await backendService.getApplicationById(user.uid);
            if (app) {
                setCurrentAppId(app.id);
                setApplications([app]);
                setRole(UserRole.CUSTOMER);
                navigate('/dashboard');
            } else {
                alert('No application found for this account. Please apply first.');
            }
        } catch (error: any) {
            console.error('Login failed:', error);
            alert(error.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    const handleAdminLogin = async (email: string, pass: string): Promise<boolean> => {
        setLoading(true);
        try {
            const user = await backendService.login(email, pass);

            // Check for custom claims first (SaaS multi-tenant auth)
            try {
                const tokenResult = await user.getIdTokenResult(true);
                const claimsRole = tokenResult.claims.role as string;

                if (claimsRole === 'super_admin') {
                    setRole(UserRole.SUPER_ADMIN);
                    await fetchAdminData(UserRole.SUPER_ADMIN);
                    navigate('/super-admin');
                    return true;
                }

                if (claimsRole === 'admin') {
                    setRole(UserRole.ADMIN);
                    await fetchAdminData(UserRole.ADMIN);
                    navigate('/admin');
                    return true;
                }
            } catch (claimsError) {
                console.log('No custom claims, falling back to legacy auth');
            }

            // Legacy fallback
            const isAdminUser = await backendService.isAdmin(user.uid);
            const isHardcodedAdmin = user.email === 'admin@nestlysuite.com';
            if (isAdminUser || isHardcodedAdmin) {
                setRole(UserRole.ADMIN);
                await fetchAdminData(UserRole.ADMIN);
                navigate('/admin');
                return true;
            }
            return false;
        } catch (e) {
            console.error("Login failed", e);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const updateApplication = async (updatedApp: Application) => {
        setApplications(apps => apps.map(a => a.id === updatedApp.id ? updatedApp : a));
        await backendService.updateApplication(updatedApp.id, updatedApp);
    };

    const addTicket = async (t: Ticket) => {
        setTickets([...tickets, t]);
        await backendService.createTicket(t);
    };

    const addInventoryItem = async (item: Omit<InventoryItem, 'id'>, cost: number) => {
        const newItem = await backendService.addInventoryItem(item);
        setInventory([...inventory, newItem]);
        if (cost > 0) {
            const tx = await backendService.addTransaction({
                date: new Date().toISOString(),
                description: `Inventory: ${item.model}`,
                amount: -cost,
                type: 'EXPENSE',
                category: 'INVENTORY'
            });
            setTransactions(prev => [tx, ...prev]);
        }
    };

    const updateInventoryItem = async (id: string, updates: any) => {
        setInventory(inv => inv.map(item => item.id === id ? { ...item, ...updates } : item));
        await backendService.updateInventoryItem(id, updates);
    };

    const handlePayment = async (amt: number, desc: string) => {
        await backendService.addTransaction({
            date: new Date().toISOString(),
            description: desc,
            amount: amt,
            type: 'INCOME',
            category: 'RENTAL'
        });
    };

    const handleAddTransaction = async (tx: any) => {
        await backendService.addTransaction(tx);
        const updated = await backendService.getTransactions();
        setTransactions(updated);
    };

    const handleSendBroadcast = async (data: any) => {
        await backendService.sendBroadcast(data);
    };

    const handleSendReminder = async (data: any) => {
        await backendService.sendReminder(data);
    };

    return (
        <ErrorBoundary>
            <div className="min-h-screen bg-slate-50 font-sans flex flex-col">
                <Navbar
                    role={role}
                    onLogout={handleLogout}
                    viewAs={viewAs}
                    setViewAs={setViewAs}
                    badges={{
                        applications: applications.filter(a => ['SUBMITTED', 'UNDER_REVIEW'].includes(a.status)).length,
                        pendingLeases: applications.filter(a => a.status === 'LEASE_SIGNED' && !a.adminSignedAt).length,
                        installations: applications.filter(a => a.status === 'SCHEDULED').length,
                        openTickets: tickets.filter(t => t.status === 'OPEN').length,
                        lowStock: inventory.filter(i => i.status === 'IN_STOCK' && !i.reservedForEventId).length <= 3 ? inventory.filter(i => i.status === 'IN_STOCK').length : 0,
                        todayRoutes: applications.filter(a => a.status === 'SCHEDULED' && a.installationDate?.startsWith(new Date().toISOString().split('T')[0])).length,
                    }}
                />
                <div className="flex flex-1 relative">
                    <div className="flex-1 flex flex-col transition-all duration-300 pt-16">
                        <div className="flex-1 p-6 overflow-x-auto">
                            <Suspense fallback={<LoadingPage />}>
                                <Routes>
                                     <Route path="/" element={
                                         window.location.hostname.includes('webtoolset.tech')
                                             ? <OEMLandingPage />
                                             : role === UserRole.GUEST ? <LandingPage /> : <Navigate to={role === UserRole.ADMIN ? "/admin" : "/dashboard"} replace />
                                     } />
                                     <Route path="/oem" element={<OEMLandingPage />} />

                                    {/* Public Content Pages */}
                                    <Route path="/about" element={<AboutPage />} />
                                    <Route path="/careers" element={<CareersPage />} />
                                    <Route path="/blog" element={<BlogPage />} />
                                    <Route path="/contact" element={<ContactPage />} />

                                    {/* Legal Pages */}
                                    <Route path="/terms" element={<LegalPage title="Terms of Service" content={TermsContent} />} />
                                    <Route path="/privacy" element={<LegalPage title="Privacy Policy" content={PrivacyContent} />} />
                                    <Route path="/cookies" element={<LegalPage title="Cookie Policy" content={CookiesContent} />} />

                                    {/* Public Lease Signing (no auth required) */}
                                    <Route path="/sign-lease" element={<SignLease />} />
                                    <Route path="/sign-lease/:id" element={<SignLease />} />

                                    {/* Public Payment Setup (no auth required) */}
                                    <Route path="/setup-payment" element={<SetupPayment />} />

                                    {/* Marketplace Lead Capture (no auth required) */}
                                    <Route path="/marketplace" element={<MarketplaceLead />} />

                                    {/* Public Lease Acceptance (no auth required) */}
                                    <Route path="/accept-lease" element={<AcceptLeaseUpdate />} />

                                    {/* Auth Pages */}
                                    <Route path="/login" element={
                                        role === UserRole.GUEST
                                            ? <CustomerLogin onLogin={handleCustomerLogin} loading={loading} />
                                            : <Navigate to={role === UserRole.CUSTOMER ? "/dashboard" : (role === UserRole.STAFF ? "/admin/maintenance" : "/admin")} />
                                    } />
                                    <Route path="/admin-login" element={role !== UserRole.GUEST ? <Navigate to="/admin" /> : <AdminLogin onLogin={handleAdminLogin} loading={loading} />} />
                                    <Route path="/apply" element={<ApplicationForm onSubmit={createApplication} />} />
                                    <Route path="/signup" element={<BusinessSignup />} />
                                    {/* Housing inquiry moved to housing-app subdomain */}

                                    {/* Customer Dashboard */}
                                    <Route path="/dashboard" element={
                                        <ProtectedRoute allowedRoles={[UserRole.CUSTOMER]} currentRole={role} loading={loading}>
                                            <ErrorBoundary>
                                                {currentAppId && applications.find(a => a.id === currentAppId) ? (
                                                    <UnifiedCustomerDashboard
                                                        app={applications.find(a => a.id === currentAppId)!}
                                                        onUpdate={updateApplication}
                                                        tickets={tickets}
                                                        onAddTicket={addTicket}
                                                        onMakePayment={handlePayment}
                                                    />
                                                ) : (
                                                    <LoadingPage message="Loading your dashboard..." />
                                                )}
                                            </ErrorBoundary>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/condition-report" element={
                                        <ProtectedRoute allowedRoles={[UserRole.CUSTOMER]} currentRole={role} loading={loading}>
                                            <ConditionReport />
                                        </ProtectedRoute>
                                    } />

                                    {/* Super Admin Routes */}
                                    <Route path="/super-admin" element={
                                        <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <SuperAdminDashboard />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/super-admin/leads" element={
                                        <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <SuperAdminLeads />
                                        </ProtectedRoute>
                                    } />

                                    {/* Admin Routes */}
                                    <Route path="/admin" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <ErrorBoundary>
                                                {/* Super Admin in platform view sees SuperAdminDashboard, otherwise CommandCenter */}
                                                {role === UserRole.SUPER_ADMIN && viewAs === 'SUPER_ADMIN' ? (
                                                    <SuperAdminDashboard />
                                                ) : (
                                                    <CommandCenter apps={applications} tickets={tickets} transactions={transactions} inventory={inventory} />
                                                )}
                                            </ErrorBoundary>
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/applications" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.STAFF, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <ApplicationManager apps={applications} onUpdate={updateApplication} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/customers" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.STAFF, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <CustomerManager apps={applications} onUpdate={updateApplication} tickets={tickets} onAddTicket={addTicket} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/inventory" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.STAFF, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <InventoryDashboard
                                                inventory={inventory}
                                                apps={applications}
                                                onAddItem={addInventoryItem}
                                                onUpdateItem={updateInventoryItem}
                                            />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/installations" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.STAFF, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <InstallationManager apps={applications} onUpdate={updateApplication} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/financials" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <FinancialDashboard transactions={transactions} onAddTransaction={handleAddTransaction} backendService={backendService} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/staff" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <StaffManager />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/messages" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.STAFF, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <MessagingCenter />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/tickets" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.STAFF, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <TicketDashboard tickets={tickets} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/crm" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <CRMDashboard customers={applications} onSendBroadcast={handleSendBroadcast} onSendReminder={handleSendReminder} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/analytics" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <AnalyticsDashboard applications={applications} transactions={transactions} tickets={tickets} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/notifications" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <NotificationCenter customers={applications} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/maintenance" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.STAFF, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <MaintenanceScheduler customers={applications} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/routes" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.STAFF, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <RouteOptimizer customers={applications} />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/lease-templates" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <LeaseTemplateEditor />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/settings" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <CompanySettings />
                                        </ProtectedRoute>
                                    } />
                                    <Route path="/admin/contact-messages" element={
                                        <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} currentRole={role} loading={loading}>
                                            <ContactMessages />
                                        </ProtectedRoute>
                                    } />

                                    {/* Rental Property Management Routes removed - now in housing-app subdomain */}
                                    {/* Access housing management at: housing.nestlysuite.com */}
                                </Routes>
                            </Suspense>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default App;